import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, RefreshCw, AlertTriangle, Check, X, Loader2 } from 'lucide-react';
import { api } from '../../api/client';

type Invoice = {
  id: string;
  invoiceNumber: string;
  invoiceTypeCode: string;
  buyerName: string;    // for inbound, holds SUPPLIER name
  buyerTaxId: string;   // supplier PIB
  totalWithVat: string;
  status: string;
  issueDate: string;
  efakturaId: number | null;
};

const statusColors: Record<string, { bg: string; color: string }> = {
  submitted: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa' },   // received, awaiting review
  sent: { bg: 'rgba(234,179,8,0.2)', color: '#facc15' },
  approved: { bg: 'rgba(34,197,94,0.2)', color: '#4ade80' },
  rejected: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5' },
  cancelled: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af' },
  storno: { bg: 'rgba(249,115,22,0.2)', color: '#fb923c' },
};

const statusLabel: Record<string, string> = {
  submitted: 'Primljeno',
  sent: 'Pregledano',
  approved: 'Prihvaćeno',
  rejected: 'Odbijeno',
  cancelled: 'Otkazano',
  storno: 'Stornirano',
};

export function PurchaseInvoicesPage({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  const fetchInvoices = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Invoice[]>(`/admin/efaktura/invoices?tenantId=${tenantId}&direction=inbound`);
      setInvoices(data);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.post<{ imported: number; updated: number; total: number }>(
        '/admin/efaktura/inbound/sync',
        { tenantId },
      );
      setNotice(`Sinhronizacija: ${res.imported} novih, ${res.updated} ažurirano (od ${res.total} izmena).`);
      await fetchInvoices();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri sinhronizaciji');
    } finally {
      setSyncing(false);
    }
  };

  const handleAccept = async (id: string) => {
    if (!window.confirm('Prihvatiti ulaznu fakturu?')) return;
    setActionBusy(id);
    setError(null);
    try {
      await api.post(`/admin/efaktura/inbound/${id}/accept`, {});
      await fetchInvoices();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri prihvatanju');
    } finally {
      setActionBusy(null);
    }
  };

  const handleReject = async (id: string) => {
    const comment = window.prompt('Razlog odbijanja:');
    if (!comment) return;
    setActionBusy(id);
    setError(null);
    try {
      await api.post(`/admin/efaktura/inbound/${id}/reject`, { comment });
      await fetchInvoices();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri odbijanju');
    } finally {
      setActionBusy(null);
    }
  };

  const formatAmount = (n: string | number) =>
    new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));

  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('sr-Latn-RS') : '-';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={handleSync} disabled={syncing || !tenantId} style={btnPrimary}>
          {syncing ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
          {syncing ? 'Sinhronizacija...' : 'Sinhronizuj sa SEF'}
        </button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
          {invoices.length} faktura
        </span>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Broj</th>
                <th style={th}>Tip</th>
                <th style={th}>Dobavljač</th>
                <th style={th}>PIB</th>
                <th style={{ ...th, textAlign: 'right' }}>Iznos</th>
                <th style={th}>Status</th>
                <th style={th}>Datum</th>
                <th style={{ ...th, width: 140 }}>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                    <FileText size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                    <div>Nema ulaznih faktura. Kliknite "Sinhronizuj sa SEF".</div>
                  </td>
                </tr>
              )}
              {invoices.map((inv) => {
                const sc = statusColors[inv.status] || statusColors.submitted;
                const canReview = inv.status === 'submitted';
                return (
                  <tr key={inv.id}>
                    <td onClick={() => navigate(`/efaktura/invoices/${inv.id}`)} style={{ ...td, color: 'var(--color-text-strong)', fontWeight: 600, cursor: 'pointer' }}>{inv.invoiceNumber || '-'}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)' }}>{inv.invoiceTypeCode}</td>
                    <td onClick={() => navigate(`/efaktura/invoices/${inv.id}`)} style={{ ...td, color: 'var(--color-text-strong)', cursor: 'pointer' }}>{inv.buyerName || '-'}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{inv.buyerTaxId || '-'}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-strong)' }}>{formatAmount(inv.totalWithVat)}</td>
                    <td style={td}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: sc.bg, color: sc.color, fontWeight: 700, textTransform: 'uppercase' }}>
                        {statusLabel[inv.status] || inv.status}
                      </span>
                    </td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{formatDate(inv.issueDate)}</td>
                    <td style={td}>
                      {canReview && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            onClick={() => handleAccept(inv.id)}
                            disabled={actionBusy === inv.id}
                            style={acceptBtn}
                            title="Prihvati"
                          >
                            {actionBusy === inv.id ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
                          </button>
                          <button
                            onClick={() => handleReject(inv.id)}
                            disabled={actionBusy === inv.id}
                            style={rejectBtn}
                            title="Odbij"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const acceptBtn: React.CSSProperties = { background: 'rgba(34,197,94,0.2)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)', padding: 6, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' };
const rejectBtn: React.CSSProperties = { background: 'rgba(239,68,68,0.2)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)', padding: 6, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const noticeBox: React.CSSProperties = { padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 16 };
